import { Component, Prop, h, Host } from '@stencil/core';

const COMPONENT_NAME = 'sn-dynamic-content';

/**
 * Dynamic content section for SignNow brand.
 * Two-column layout: 8-column main area (heading + raw HTML body) and 4-column sidebar.
 * The heading is inline-editable via the `heading` slot. The HTML body is backend-generated
 * markup delivered via the `raw_html` attribute and rendered via innerHTML.
 * The sidebar is a pure slot pass-through for any supplementary content.
 *
 * @product signnow
 * @source dynamiccontent
 * @version 2.1.0
 * @category SignNow
 * @contentConfig Dynamic legal / long-form HTML content with optional sidebar
 * @slot heading - <me-heading tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading
 * @slot body - {required}, {raw-html} - HTML body content
 * @slot sidebar - Optional sidebar content
 * @example
 *   <sn-dynamic-content-type-1
 *     raw-html="<p>Electronic signatures are legally binding in most countries worldwide. They are governed by specific laws and regulations in each country.</p>"
 *   >
 *     <me-heading slot="heading" variant="2" tag="h2">Electronic Signature Legality</me-heading>
 *   </sn-dynamic-content-type-1>
 */
@Component({
  tag: 'sn-dynamic-content-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNDynamicContent {
  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Raw HTML body content rendered via innerHTML.
   * When provided, takes precedence over the `body` slot.
   */
  @Prop() rawHtml?: string;

  /**
   * Horizontal alignment of the heading and body content.
   * `left` (default) keeps content left-aligned; `center` centers both the heading and the body text.
   * @default left
   */
  @Prop() align: 'left' | 'center' = 'left';

  render() {
    const mainClass = `${COMPONENT_NAME}__main${this.align === 'center' ? ` ${COMPONENT_NAME}__main--center` : ''}`;

    return (
      <Host data-color-mode={this.colorMode}>
        <me-section yGap="2xl" yGapLg="5xl" role="region" aria-label="Dynamic Content">
          <me-row yGap="none" yGapMd="none" yGapLg="none">
            <me-column span="12" spanMd={this.align === 'center' ? '12' : '8'} class={mainClass}>
              <slot name="heading"></slot>
              {this.rawHtml
                ? <div class={COMPONENT_NAME} innerHTML={this.rawHtml}></div>
                : <slot name="body"></slot>}
            </me-column>
            <me-column span="12" spanMd="4" class={`${COMPONENT_NAME}__sidebar`}>
              <slot name="sidebar"></slot>
            </me-column>
          </me-row>
        </me-section>
      </Host>
    );
  }
}
